import { Project } from '../types';

export function formatProjectDate(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function isPastEndDate(value: string | null | undefined): boolean {
  if (value == null || value === '') return false;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return false;
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today > endDay;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function daysUntilEndDate(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  if (isPastEndDate(value)) return null;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return null;
  const endDay = startOfLocalDay(end);
  const today = startOfLocalDay(new Date());
  return Math.round((endDay.getTime() - today.getTime()) / MS_PER_DAY);
}

export function scheduleGap(project: Project): number | null {
  if (!project.startDate || !project.endDate) return null;
  const start = new Date(project.startDate);
  const end = new Date(project.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const s = startOfLocalDay(start).getTime();
  const e = startOfLocalDay(end).getTime();
  if (e <= s) return null;
  const t = startOfLocalDay(new Date()).getTime();
  const elapsedFrac = Math.min(1, Math.max(0, (t - s) / (e - s)));
  const completionFrac = (project.taskCompletion?.percent ?? 0) / 100;
  return completionFrac - elapsedFrac;
}

export type ProjectCardTheme = 'critical' | 'warning' | 'neutral' | 'positive' | 'success';

export const PROJECT_CARD_THEME: Record<
  ProjectCardTheme,
  {
    bg: string;
    borderWidth: number;
    border: string;
    borderHover: string;
    topFrom: string;
    topTo: string;
    progressFrom: string;
    progressTo: string;
  }
> = {
  critical: {
    bg: 'red.50',
    borderWidth: 2,
    border: 'red.300',
    borderHover: 'red.400',
    topFrom: '#991b1b',
    topTo: '#ef4444',
    progressFrom: '#991b1b',
    progressTo: '#ef4444',
  },
  warning: {
    bg: 'orange.50',
    borderWidth: 2,
    border: 'orange.300',
    borderHover: 'orange.400',
    topFrom: '#c2410c',
    topTo: '#fb923c',
    progressFrom: '#c2410c',
    progressTo: '#fb923c',
  },
  neutral: {
    bg: 'white',
    borderWidth: 1,
    border: 'gray.200',
    borderHover: 'blue.300',
    topFrom: '#273f96',
    topTo: '#1369b3',
    progressFrom: '#273f96',
    progressTo: '#1369b3',
  },
  positive: {
    bg: 'teal.50',
    borderWidth: 1,
    border: 'teal.200',
    borderHover: 'teal.400',
    topFrom: '#0f766e',
    topTo: '#2dd4bf',
    progressFrom: '#0f766e',
    progressTo: '#2dd4bf',
  },
  success: {
    bg: 'green.50',
    borderWidth: 1,
    border: 'green.200',
    borderHover: 'green.400',
    topFrom: '#166534',
    topTo: '#22c55e',
    progressFrom: '#166534',
    progressTo: '#22c55e',
  },
};

export function getProjectCardTheme(project: Project): ProjectCardTheme {
  if (isPastEndDate(project.endDate)) return 'critical';

  const pct = project.taskCompletion?.percent ?? 0;
  if (pct >= 100) return 'success';

  const daysLeft = daysUntilEndDate(project.endDate);
  const gap = scheduleGap(project);

  if (gap !== null) {
    if (gap < -0.12) return 'warning';
    if (gap >= 0.1 && pct >= 35) return 'positive';
  }

  if (daysLeft !== null) {
    if (daysLeft <= 1 && pct < 98) return 'warning';
    if (daysLeft <= 7 && pct < 40) return 'warning';
    if (daysLeft <= 14 && pct < 20) return 'warning';
    if (daysLeft >= 21 && pct >= 70) return 'positive';
  }

  if (pct >= 85) return 'positive';

  return 'neutral';
}

export function getProjectStatusBadges(theme: ProjectCardTheme, project: Project) {
  const badges: Array<{ label: string; colorScheme: string }> = [];
  if (theme === 'critical') badges.push({ label: 'Past end date', colorScheme: 'red' });
  if (theme === 'warning') badges.push({ label: 'At risk', colorScheme: 'orange' });
  if (theme === 'success') badges.push({ label: 'Complete', colorScheme: 'green' });
  badges.push({
    label: project.status === 'archived' ? 'Archived' : 'Active',
    colorScheme: project.status === 'archived' ? 'gray' : 'green',
  });
  return badges;
}
